// Transform gizmo wrapper. Wraps three.js TransformControls and handles
// multi-select via a hidden pivot Object3D placed at the centroid of the
// selection. On mouseDown we snapshot each target's matrix relative to the
// pivot's inverse-world matrix; on objectChange we re-apply
// `pivotWorld * relativeMatrix` to each target; on mouseUp we commit the
// resulting transforms back to JSON via state.commitTransform.

import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import type { EditorState } from './EditorState';
import type { EditorTool } from './EditorTool';
import type { MapJSON, ObjectNode, Vec3 } from '../../maps/MapJSON';

const TINY = 1e-5;

export interface EditorGizmoCallbacks {
  /** Called when the gizmo enters / leaves a drag — host can disable the fly
   *  controls so WASD doesn't fight the gizmo. */
  onDraggingChanged?(dragging: boolean): void;
}

export class EditorGizmo {
  private tc: TransformControls;
  private helperRoot: THREE.Object3D;
  private pivot = new THREE.Group();
  private offsets = new Map<string, THREE.Matrix4>();
  private active: string[] = [];
  private objectsById: ReadonlyMap<string, THREE.Object3D> = new Map();
  private state: EditorState;
  private tool: EditorTool;
  private cbs: EditorGizmoCallbacks;
  private unsubTool: () => void;

  private tmpInv = new THREE.Matrix4();
  private tmpMat = new THREE.Matrix4();
  private tmpPos = new THREE.Vector3();
  private tmpQuat = new THREE.Quaternion();
  private tmpScale = new THREE.Vector3();

  constructor(
    camera: THREE.Camera, dom: HTMLElement, scene: THREE.Scene,
    state: EditorState, tool: EditorTool, cbs: EditorGizmoCallbacks = {}
  ) {
    this.state = state;
    this.tool = tool;
    this.cbs = cbs;
    this.tc = new TransformControls(camera, dom);
    // Three v0.184 returns the visible helper from `getHelper()`; older
    // builds add the controls itself to the scene.
    const tcAny = this.tc as TransformControls & { getHelper?: () => THREE.Object3D };
    this.helperRoot = tcAny.getHelper?.() ?? (this.tc as unknown as THREE.Object3D);
    scene.add(this.helperRoot);
    scene.add(this.pivot);
    this.tc.attach(this.pivot);
    this.helperRoot.visible = false;

    this.tc.addEventListener('mouseDown', this.onMouseDown);
    this.tc.addEventListener('objectChange', this.onObjectChange);
    this.tc.addEventListener('mouseUp', this.onMouseUp);
    this.tc.addEventListener('dragging-changed', (e) => {
      this.cbs.onDraggingChanged?.(!!(e as unknown as { value: boolean }).value);
    });

    this.unsubTool = this.tool.subscribe(() => this.applyToolState());
    this.applyToolState();
  }

  setObjectsById(map: ReadonlyMap<string, THREE.Object3D>): void {
    this.objectsById = map;
  }

  setSelection(ids: string[]): void {
    this.active = ids;
    if (ids.length === 0) {
      this.helperRoot.visible = false;
      return;
    }
    // Centroid pivot
    const center = new THREE.Vector3();
    let count = 0;
    for (const id of ids) {
      const o = this.objectsById.get(id);
      if (!o) continue;
      o.updateWorldMatrix(true, false);
      const p = new THREE.Vector3().setFromMatrixPosition(o.matrixWorld);
      center.add(p);
      count += 1;
    }
    if (count === 0) {
      this.helperRoot.visible = false;
      return;
    }
    center.multiplyScalar(1 / count);
    this.pivot.position.copy(center);
    this.pivot.quaternion.identity();
    this.pivot.scale.set(1, 1, 1);
    this.pivot.updateMatrixWorld(true);
    // Multi-select forces world-space gizmo (pivot has no rotation anyway).
    this.tc.space = ids.length > 1 ? 'world' : this.tool.get().space;
    this.helperRoot.visible = this.tool.get().mode !== 'select';
  }

  dispose(): void {
    this.tc.detach();
    this.tc.removeEventListener('mouseDown', this.onMouseDown);
    this.tc.removeEventListener('objectChange', this.onObjectChange);
    this.tc.removeEventListener('mouseUp', this.onMouseUp);
    this.helperRoot.parent?.remove(this.helperRoot);
    this.pivot.parent?.remove(this.pivot);
    (this.tc as TransformControls & { dispose?: () => void }).dispose?.();
    this.unsubTool();
  }

  private applyToolState(): void {
    const t = this.tool.get();
    if (t.mode === 'select' || t.mode === 'control') {
      // 'control' mode is fly-cam only — gizmo handles would steal focus
      // from the cursor-lock workflow. 'select' mode shows no gizmo by
      // design (transform happens via inspector / hotkeys / Translate).
      this.helperRoot.visible = false;
      this.tc.enabled = false;
    } else {
      this.tc.enabled = true;
      this.tc.setMode(t.mode === 'translate' ? 'translate' : t.mode === 'rotate' ? 'rotate' : 'scale');
      this.tc.space = this.active.length > 1 ? 'world' : t.space;
      this.tc.showX = t.axisLock.x;
      this.tc.showY = t.axisLock.y;
      this.tc.showZ = t.axisLock.z;
      this.tc.setTranslationSnap(t.snap.enabled ? t.snap.translate : null);
      this.tc.setRotationSnap(t.snap.enabled ? (t.snap.rotateDeg * Math.PI / 180) : null);
      this.tc.setScaleSnap(t.snap.enabled ? t.snap.scale : null);
      this.helperRoot.visible = this.active.length > 0;
    }
  }

  private onMouseDown = (): void => {
    this.offsets.clear();
    this.pivot.updateMatrixWorld(true);
    this.tmpInv.copy(this.pivot.matrixWorld).invert();
    for (const id of this.active) {
      const o = this.objectsById.get(id);
      if (!o) continue;
      o.updateWorldMatrix(true, false);
      const rel = new THREE.Matrix4().multiplyMatrices(this.tmpInv, o.matrixWorld);
      this.offsets.set(id, rel);
    }
  };

  private onObjectChange = (): void => {
    this.pivot.updateMatrixWorld(true);
    for (const id of this.active) {
      const o = this.objectsById.get(id);
      const rel = this.offsets.get(id);
      if (!o || !rel) continue;
      this.tmpMat.multiplyMatrices(this.pivot.matrixWorld, rel);
      const parentInv = new THREE.Matrix4();
      if (o.parent) {
        o.parent.updateWorldMatrix(true, false);
        parentInv.copy(o.parent.matrixWorld).invert();
      }
      this.tmpMat.premultiply(parentInv);
      this.tmpMat.decompose(this.tmpPos, this.tmpQuat, this.tmpScale);
      o.position.copy(this.tmpPos);
      o.quaternion.copy(this.tmpQuat);
      o.scale.copy(this.tmpScale);
    }
  };

  private onMouseUp = (): void => {
    if (this.active.length === 0) return;
    const ids = this.active.slice();
    const updates = new Map<string, { p: Vec3; r: Vec3; s: Vec3 }>();
    for (const id of ids) {
      const o = this.objectsById.get(id);
      if (!o) continue;
      const eu = new THREE.Euler().setFromQuaternion(o.quaternion, 'XYZ');
      updates.set(id, {
        p: [o.position.x, o.position.y, o.position.z],
        r: [eu.x, eu.y, eu.z],
        s: [o.scale.x, o.scale.y, o.scale.z]
      });
    }
    this.state.commitTransform(ids, (json) => mutateTransforms(json, updates));
  };
}

function mutateTransforms(json: MapJSON, updates: Map<string, { p: Vec3; r: Vec3; s: Vec3 }>): MapJSON {
  if (!json.objects) return json;
  let dirty = false;
  const walk = (nodes: ObjectNode[]): ObjectNode[] => {
    let changed = false;
    const out = nodes.map((n) => {
      const u = n.id ? updates.get(n.id) : undefined;
      let next: ObjectNode = n;
      if (u) {
        const t = next.transform ?? {};
        next = { ...next, transform: { ...t, p: u.p, r: u.r, s: zeroOnePass(u.s, t.s) } } as ObjectNode;
        changed = true; dirty = true;
      }
      if (next.type === 'group' && next.children?.length) {
        const sub = walk(next.children);
        if (sub !== next.children) {
          next = { ...next, children: sub };
          changed = true;
        }
      }
      return next;
    });
    return changed ? out : nodes;
  };
  const nextObjects = walk(json.objects);
  if (!dirty) return json;
  return { ...json, objects: nextObjects };
}

function zeroOnePass(s: Vec3, prev: Vec3 | number | undefined): Vec3 | number {
  // Preserve scalar scale form when the user hasn't introduced anisotropy.
  if (typeof prev === 'number') {
    if (Math.abs(s[0] - s[1]) < TINY && Math.abs(s[1] - s[2]) < TINY) return s[0]!;
  }
  return s;
}
